import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { GridPaginationModel } from "@mui/x-data-grid";
import {
  listarLancamentosFinanceiro,
  obterResumoFinanceiro,
} from "services/lojaFinanceiro";
import type {
  ILancamentoFinanceiroRow,
  IFinanceiroResumoResponse,
  OrigemLancamentoFinanceiro,
  StatusLancamentoFinanceiro,
  TipoLancamentoFinanceiro,
} from "interfaces/loja/financeiro";
import { readFinanceiroDashboardPeriodo } from "layouts/pages/loja/financeiro/financeiro-periodo-storage";
import { setMessage, useMaterialUIController } from "context";

const DEFAULT_PAGE_SIZE = 20;

export type FinanceiroLojaFilters = {
  dataInicio: string;
  dataFim: string;
  tipo: string;
  origem: string;
  status: string;
  pedidoId: string;
  pagamentoId: string;
};

const emptyFilters: FinanceiroLojaFilters = {
  dataInicio: "",
  dataFim: "",
  tipo: "",
  origem: "",
  status: "",
  pedidoId: "",
  pagamentoId: "",
};

/** Formato YYYY-MM-DD para inputs type="date". */
function formatDateYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function resolveInitialFilters(): FinanceiroLojaFilters {
  if (typeof window !== "undefined") {
    const qs = new URLSearchParams(window.location.search);
    const dataInicio = qs.get("dataInicio")?.trim();
    const dataFim = qs.get("dataFim")?.trim();
    if (dataInicio && dataFim) {
      return {
        ...emptyFilters,
        dataInicio,
        dataFim,
      };
    }
  }
  return defaultFinanceiroMonthFilters();
}

/** Primeiro e último dia do mês corrente (calendário local). */
export function defaultFinanceiroMonthFilters(): FinanceiroLojaFilters {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    ...emptyFilters,
    dataInicio: formatDateYMD(start),
    dataFim: formatDateYMD(end),
  };
}

export type UseFinanceiroLojaResult = {
  appliedFilters: FinanceiroLojaFilters;
  pendingFilters: FinanceiroLojaFilters;
  setPendingFilter: <K extends keyof FinanceiroLojaFilters>(
    field: K,
    value: FinanceiroLojaFilters[K]
  ) => void;
  applyFilters: () => void;
  applyDashboardPeriodo: () => boolean;
  clearFilters: () => void;
  page: number;
  pageSize: number;
  rows: ILancamentoFinanceiroRow[];
  total: number;
  resumo: IFinanceiroResumoResponse | null;
  gridLoading: boolean;
  refetch: () => void;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  setPageSize: (size: number) => void;
};

function buildListParams(
  applied: FinanceiroLojaFilters,
  page: number,
  pageSize: number
) {
  return {
    page,
    limit: pageSize,
    tipo: (applied.tipo || undefined) as TipoLancamentoFinanceiro | undefined,
    status: (applied.status || undefined) as
      | StatusLancamentoFinanceiro
      | undefined,
    origem: (applied.origem || undefined) as
      | OrigemLancamentoFinanceiro
      | undefined,
    pedidoId: applied.pedidoId.trim() || undefined,
    pagamentoId: applied.pagamentoId.trim() || undefined,
    dataInicio: applied.dataInicio || undefined,
    dataFim: applied.dataFim || undefined,
  };
}

function buildResumoParams(applied: FinanceiroLojaFilters) {
  return {
    dataInicio: applied.dataInicio || undefined,
    dataFim: applied.dataFim || undefined,
  };
}

export function useFinanceiroLoja(): UseFinanceiroLojaResult {
  const [, dispatchCtx] = useMaterialUIController();

  const [pendingFilters, setPendingFilters] = useState<FinanceiroLojaFilters>(
    () => resolveInitialFilters(),
  );
  const [appliedFilters, setAppliedFilters] = useState<FinanceiroLojaFilters>(
    () => resolveInitialFilters(),
  );

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [rows, setRows] = useState<ILancamentoFinanceiroRow[]>([]);
  const [total, setTotal] = useState(0);
  const [resumo, setResumo] = useState<IFinanceiroResumoResponse | null>(null);
  const [gridLoading, setGridLoading] = useState(true);
  const [manualRefresh, setManualRefresh] = useState(0);

  const listParams = useMemo(
    () => buildListParams(appliedFilters, page, pageSize),
    [appliedFilters, page, pageSize]
  );

  const resumoParams = useMemo(
    () => buildResumoParams(appliedFilters),
    [appliedFilters]
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setGridLoading(true);
      try {
        const [listResp, resumoResp] = await Promise.all([
          listarLancamentosFinanceiro(listParams),
          obterResumoFinanceiro(resumoParams),
        ]);

        if (cancelled) return;

        if (listResp.status === 403 || resumoResp.status === 403) {
          setMessage(dispatchCtx, {
            open: true,
            severity: "warning",
            text: "Você não tem permissão para acessar o financeiro da loja.",
          });
          setRows([]);
          setTotal(0);
          setResumo(null);
          return;
        }

        if (listResp.status !== 200) {
          setRows([]);
          setTotal(0);
        } else {
          setRows(listResp.data.data ?? []);
          setTotal(listResp.data.meta?.totalRecord ?? 0);
        }

        if (resumoResp.status === 200) {
          setResumo(resumoResp.data);
        } else {
          setResumo(null);
        }
      } catch (error: unknown) {
        if (cancelled) return;
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        if (status === 403) {
          setMessage(dispatchCtx, {
            open: true,
            severity: "warning",
            text: "Você não tem permissão para acessar o financeiro da loja.",
          });
        } else {
          const message =
            error instanceof Error
              ? error.message
              : "Falha ao carregar financeiro da loja.";
          setMessage(dispatchCtx, {
            open: true,
            severity: "error",
            text: message,
          });
        }
        setRows([]);
        setTotal(0);
        setResumo(null);
      } finally {
        if (!cancelled) setGridLoading(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [listParams, resumoParams, dispatchCtx, manualRefresh]);

  const refetch = useCallback(() => {
    setManualRefresh((n) => n + 1);
  }, []);

  const setPendingFilter = useCallback(
    <K extends keyof FinanceiroLojaFilters>(
      field: K,
      value: FinanceiroLojaFilters[K]
    ) => {
      setPendingFilters((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const applyFilters = useCallback(() => {
    setAppliedFilters({ ...pendingFilters });
    setPage(1);
  }, [pendingFilters]);

  const applyDashboardPeriodo = useCallback(() => {
    const periodo = readFinanceiroDashboardPeriodo();
    if (!periodo) return false;
    const next = {
      ...pendingFilters,
      dataInicio: periodo.dataInicio,
      dataFim: periodo.dataFim,
    };
    setPendingFilters(next);
    setAppliedFilters(next);
    setPage(1);
    return true;
  }, [pendingFilters]);

  const clearFilters = useCallback(() => {
    const defaults = defaultFinanceiroMonthFilters();
    setPendingFilters(defaults);
    setAppliedFilters(defaults);
    setPage(1);
  }, []);

  const setPageSizeFn = useCallback((size: number) => {
    setPageSize(size);
    setPage(1);
  }, []);

  const onPaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      const nextPage = model.page + 1;
      const nextSize = model.pageSize;
      if (nextSize !== pageSize) {
        setPageSize(nextSize);
        setPage(1);
      } else if (nextPage !== page) {
        setPage(nextPage);
      }
    },
    [page, pageSize]
  );

  return {
    appliedFilters,
    pendingFilters,
    setPendingFilter,
    applyFilters,
    applyDashboardPeriodo,
    clearFilters,
    page,
    pageSize,
    rows,
    total,
    resumo,
    gridLoading,
    refetch,
    onPaginationModelChange,
    setPageSize: setPageSizeFn,
  };
}
